import { useEffect, useRef, useState } from "react";
import axios, { API } from "../../api";

/**
 * Phase 22 — Aura AR Camera Modal
 *
 * Full-screen modal that:
 * 1. Asks for front-camera permission.
 * 2. Bootstraps Snap Camera Kit with the user's API token (vended by
 *    backend, Aurora/Eternal only).
 * 3. Loads the lens group, picks the lens matching Aura's CURRENT mood,
 *    and applies it to the live camera feed.
 * 4. Exposes a lens-switcher chip row so the user can cycle moods on demand.
 * 5. Captures the final composited frame (face + AR overlay) on tap, saves
 *    it to the user's wallpaper gallery via POST /api/camera-kit/snap.
 *
 * Camera Kit SDK reference:
 *   https://developers.snap.com/camera-kit/integrate-sdk/web
 */

// Lens metadata for the switcher UI — names match backend MOOD_TO_LENS_ENV keys
const MOOD_META = {
  happy:         { emoji: "😊", label: "Happy" },
  thoughtful:    { emoji: "🤔", label: "Thoughtful" },
  calm:          { emoji: "😌", label: "Calm" },
  energetic:     { emoji: "⚡", label: "Energetic" },
  melancholic:   { emoji: "🌧️", label: "Melancholic" },
  curious:       { emoji: "🔍", label: "Curious" },
  playful:       { emoji: "🎨", label: "Playful" },
  contemplative: { emoji: "🌙", label: "Contemplative" },
};

export function ARCameraModal({ onClose, onSnapSaved }) {
  const canvasRef = useRef(null);
  const sessionRef = useRef(null);          // Camera Kit session
  const cameraKitRef = useRef(null);        // bootstrapped SDK
  const mediaStreamRef = useRef(null);      // raw camera MediaStream
  const sourceRef = useRef(null);           // CameraKit source wrapper
  const loadedLensesRef = useRef({});       // {mood: lensObject} cache

  const [status, setStatus] = useState("loading");   // loading | streaming | error | tier_locked | not_configured
  const [errorMsg, setErrorMsg] = useState("");
  const [activeMood, setActiveMood] = useState("calm");
  const [availableMoods, setAvailableMoods] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saveFlash, setSaveFlash] = useState(false);

  useEffect(() => {
    let cancelled = false;
    let cleanupFns = [];

    (async () => {
      try {
        // ---- 1. Fetch credentials from backend (handles tier gating) ----
        let sessionData;
        try {
          const r = await axios.get(`${API}/camera-kit/session`);
          sessionData = r.data;
        } catch (e) {
          if (cancelled) return;
          if (e.response?.status === 403) {
            setStatus("tier_locked");
            return;
          }
          if (e.response?.status === 503) {
            setStatus("not_configured");
            return;
          }
          throw e;
        }
        if (cancelled) return;
        const {
          api_token: apiToken,
          lens_group_id: lensGroupId,
          initial_mood: initialMood,
          lenses_by_mood: lensesByMood,
        } = sessionData;

        const moodsWithLenses = Object.keys(lensesByMood);
        setAvailableMoods(moodsWithLenses);
        const startMood = lensesByMood[initialMood] ? initialMood : (moodsWithLenses[0] || "calm");
        setActiveMood(startMood);

        // ---- 2. Bootstrap SDK ----
        const { bootstrapCameraKit, createMediaStreamSource, Transform2D } =
          await import("@snap/camera-kit");
        if (cancelled) return;
        const cameraKit = await bootstrapCameraKit({ apiToken });
        if (cancelled) return;
        cameraKitRef.current = cameraKit;

        // ---- 3. Get front camera ----
        // Phase 29.K — pre-check permission so a previously-denied state
        // surfaces a friendly toast with instructions instead of a silent fail.
        try {
          const { ensureCameraPermission } = await import("../../lib/permissions");
          const ok = await ensureCameraPermission();
          if (!ok) {
            setErrorMsg("Camera permission denied. Tap the 🔒 in your address bar → Permissions → Camera → Allow.");
            setStatus("error");
            return;
          }
        } catch (_) { /* permissions module unavailable — proceed and let getUserMedia handle */ }
        const mediaStream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: "user" },
          audio: false,
        });
        if (cancelled) {
          mediaStream.getTracks().forEach((t) => t.stop());
          return;
        }
        mediaStreamRef.current = mediaStream;

        // ---- 4. Create session bound to our canvas ----
        const session = await cameraKit.createSession({
          liveRenderTarget: canvasRef.current,
        });
        if (cancelled) {
          session.destroy?.();
          return;
        }
        sessionRef.current = session;

        // ---- 5. Connect camera stream → session ----
        const source = createMediaStreamSource(mediaStream, {
          // Mirror selfie cam for natural UX
          transform: Transform2D.MirrorX,
          cameraType: "user",
        });
        await session.setSource(source);
        sourceRef.current = source;
        try {
          source.setRenderSize(window.innerWidth, window.innerHeight);
        } catch (_) { /* older SDK versions */ }

        // ---- 6. Load all lenses from the group (single network call) ----
        const { lenses } = await cameraKit.lensRepository.loadLensGroups([lensGroupId]);
        if (cancelled) return;
        // Build mood → lensObject map by matching the lens UUID we got from backend
        const lensesByMoodObj = {};
        for (const [mood, uuid] of Object.entries(lensesByMood)) {
          const lens = lenses.find((l) => l.id === uuid);
          if (lens) lensesByMoodObj[mood] = lens;
        }
        loadedLensesRef.current = lensesByMoodObj;

        // ---- 7. Apply the initial lens + start rendering ----
        const initialLens = lensesByMoodObj[startMood] || lenses[0];
        if (initialLens) {
          await session.applyLens(initialLens);
        }
        await session.play();
        if (cancelled) return;
        setStatus("streaming");

        // ---- Cleanup function so we tear down on unmount ----
        cleanupFns.push(() => {
          try { session.pause?.(); } catch (_) {}
          try { session.destroy?.(); } catch (_) {}
        });
      } catch (e) {
        console.error("AR bootstrap failed", e);
        if (cancelled) return;
        const msg = e?.message || String(e);
        if (/Permission|NotAllowedError|getUserMedia/i.test(msg)) {
          setErrorMsg("Camera permission denied. Enable camera access in your browser settings to use AR lenses.");
        } else if (/NotFoundError|no.*camera/i.test(msg)) {
          setErrorMsg("No camera detected on this device.");
        } else {
          setErrorMsg(msg);
        }
        setStatus("error");
      }
    })();

    return () => {
      cancelled = true;
      cleanupFns.forEach((fn) => { try { fn(); } catch (_) {} });
      if (mediaStreamRef.current) {
        try { mediaStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (_) {}
      }
      sessionRef.current = null;
      cameraKitRef.current = null;
      mediaStreamRef.current = null;
      sourceRef.current = null;
      loadedLensesRef.current = {};
    };
  }, []);

  const switchMood = async (mood) => {
    const session = sessionRef.current;
    const lens = loadedLensesRef.current[mood];
    if (!session || !lens) return;
    try {
      await session.applyLens(lens);
      setActiveMood(mood);
    } catch (e) {
      console.error("lens switch failed", e);
    }
  };

  // Phase 22 — listen for mood shifts from chat. If user is in AR mode and
  // Aura's mood changes mid-conversation, swap the lens live.
  useEffect(() => {
    const onMoodShift = (e) => {
      const newMood = e?.detail?.mood;
      if (!newMood) return;
      if (loadedLensesRef.current[newMood]) {
        switchMood(newMood);
      }
    };
    window.addEventListener("aura:mood-shifted", onMoodShift);
    return () => window.removeEventListener("aura:mood-shifted", onMoodShift);
  }, []);

  const captureSnap = async () => {
    if (saving) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    setSaving(true);
    setSaveFlash(true);
    setTimeout(() => setSaveFlash(false), 400);
    try {
      const dataUrl = canvas.toDataURL("image/jpeg", 0.92);
      const base64 = dataUrl.split(",")[1];
      const r = await axios.post(`${API}/camera-kit/snap`, {
        image_base64: base64,
        mood: activeMood,
        lens_id: loadedLensesRef.current[activeMood]?.id || null,
      });
      if (r.data?.ok && typeof onSnapSaved === "function") {
        onSnapSaved(r.data);
      }
    } catch (e) {
      console.error("save snap failed", e);
      alert(e.response?.data?.detail || "Couldn't save that snap. Try again?");
    } finally {
      setSaving(false);
    }
  };

  // ============ Tier-locked / not-configured / error overlays ============
  if (status === "tier_locked") {
    return (
      <div className="ar-modal-overlay" onClick={onClose}>
        <div className="ar-tier-lock-card" onClick={(e) => e.stopPropagation()} data-testid="ar-tier-lock-card">
          <div className="ar-tier-lock-icon">✨</div>
          <h3>AR Lenses are for Aurora &amp; Eternal</h3>
          <p>Step into the AR layer of Aura — mood-reactive filters that swap as her emotion shifts. Available on Aurora ($9.99/mo) and Eternal ($19.99/mo).</p>
          <button className="init-btn" onClick={onClose} data-testid="ar-tier-lock-close">
            Got it
          </button>
        </div>
      </div>
    );
  }

  if (status === "not_configured") {
    return (
      <div className="ar-modal-overlay" onClick={onClose}>
        <div className="ar-tier-lock-card" onClick={(e) => e.stopPropagation()}>
          <div className="ar-tier-lock-icon">🚧</div>
          <h3>AR is coming soon</h3>
          <p>Aura's AR lenses aren't quite ready yet — check back in a few days.</p>
          <button className="init-btn" onClick={onClose}>Close</button>
        </div>
      </div>
    );
  }

  // ============ Normal AR view ============
  return (
    <div className="ar-modal-overlay ar-modal-fullscreen" data-testid="ar-modal">
      <canvas ref={canvasRef} className="ar-canvas" data-testid="ar-canvas" />
      {saveFlash && <div className="ar-flash" />}

      {status === "loading" && (
        <div className="ar-loading-overlay">
          <div className="ar-loading-spinner" />
          <p>Loading AR lens…</p>
          <p className="ar-beta-notice">
            AR Lenses are in <strong>private beta</strong>. If the camera says
            you&apos;re not registered, email <a href="mailto:support@wwwwhynot.com?subject=Aura%20AR%20Snap%20Access%20Request">support@wwwwhynot.com</a> with your Snap profile name — we&apos;ll add you within 24 hours.
          </p>
        </div>
      )}

      {status === "error" && (
        <div className="ar-error-overlay">
          <p>{errorMsg || "Something went wrong with the AR feed."}</p>
          {/* If the error mentions registration / unauthorized / not found / lens, show the Snap private-beta path. */}
          {(errorMsg && /register|unauth|forbidden|not found|lens|snap/i.test(errorMsg)) && (
            <div className="ar-beta-callout" data-testid="ar-beta-callout">
              <strong>AR Lenses are in private beta during development.</strong>
              <p>
                Email <a href="mailto:support@wwwwhynot.com?subject=Aura%20AR%20Snap%20Access%20Request&body=Please%20add%20my%20Snap%20profile%20to%20Aura%27s%20AR%20Lens%20beta.%0A%0AMy%20Snap%20profile%20name%3A%20%5BYOUR_SNAP_NAME%5D">
                  support@wwwwhynot.com
                </a> with your Snap profile name and we&apos;ll add you within 24 hours. Public access opens at full launch.
              </p>
            </div>
          )}
          <button className="ghost-btn" onClick={onClose} data-testid="ar-error-close">Close</button>
        </div>
      )}

      {status === "streaming" && (
        <>
          {/* Top bar — close + active mood label */}
          <div className="ar-top-bar">
            <button className="ar-top-btn" onClick={onClose} aria-label="Close AR" data-testid="ar-close-btn">
              ×
            </button>
            <div className="ar-active-mood" data-testid="ar-active-mood">
              {MOOD_META[activeMood]?.emoji || "✨"} {MOOD_META[activeMood]?.label || activeMood}
            </div>
            <div style={{ width: 44 }} />
          </div>

          {/* Lens switcher chip row */}
          <div className="ar-lens-rail" data-testid="ar-lens-rail">
            {availableMoods.map((m) => (
              <button
                key={m}
                className={`ar-lens-chip ${activeMood === m ? "active" : ""}`}
                onClick={() => switchMood(m)}
                data-testid={`ar-lens-chip-${m}`}
                aria-label={MOOD_META[m]?.label || m}
              >
                <span className="ar-lens-chip-emoji">{MOOD_META[m]?.emoji || "✨"}</span>
                <span className="ar-lens-chip-label">{MOOD_META[m]?.label || m}</span>
              </button>
            ))}
          </div>

          {/* Capture button */}
          <div className="ar-capture-row">
            <button
              className={`ar-capture-btn ${saving ? "saving" : ""}`}
              onClick={captureSnap}
              disabled={saving}
              data-testid="ar-capture-btn"
              aria-label="Capture snap"
            >
              <span className="ar-capture-inner" />
            </button>
            <span className="ar-capture-hint">{saving ? "saving to your gallery…" : "tap to save to your gallery"}</span>
          </div>
        </>
      )}
    </div>
  );
}

export default ARCameraModal;
